import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState, Subtask } from 'constants/interfaces'
import { resolveResultOccurrence } from '../../model/resultRequests'
import { fetchCourseProgress, saveCourseProgress } from '../api/courseProgress'
import {
  appMessage,
  AppMessageType,
  courseOrigin,
  courseStorageKey,
  ICourseMessage,
  parseCourseMessage,
  toBridgeError,
} from '../model/courseBridge'
import { courseProgressRequest } from '../model/courseProgress'
import { useTaskOverviewContext } from '../TaskOverviewStackProvider'

// how long the player gets to save what is left when the viewer closes (it answers sooner when done)
export const COURSE_FLUSH_TIMEOUT_MS = 3500

/**
 * The web app's side of the course bridge (model/courseBridge): answers the player in the course iframe.
 * `ready` -> loads the progress and sends `init`; `load`/`save` -> calls /course-progress and answers;
 * `finished`/`close` from the player. Closing first asks the player to flush. While the viewer is open,
 * only this frame on the course's origin is listened to; answers that arrive after close are dropped.
 */
export function useCourseHost(subtask: Subtask, frameRef: RefObject<HTMLIFrameElement>, onClose: () => void) {
  const { task, resultContextId } = useTaskOverviewContext()
  const rid = useSelector((state: RootState) => state.config.config.rid)
  const uid = useSelector((state: RootState) => state.auth.uid)
  const firstName = useSelector((state: RootState) => state.config.config.first_name)
  const lastName = useSelector((state: RootState) => state.config.config.last_name)

  const [finished, setFinished] = useState(false)
  const [closing, setClosing] = useState(false)

  // false once the viewer is closed: late answers must not reach a course that is gone
  const activeRef = useRef(true)
  const initSentRef = useRef(false)
  const flushRef = useRef<{ id: string; timer: number } | null>(null)
  // the latest callbacks, for the message listener (kept current by the first effect below)
  const onCloseRef = useRef(onClose)
  const handleRef = useRef<(message: ICourseMessage) => void>(() => undefined)

  const origin = courseOrigin(subtask.external_link)
  const request = courseProgressRequest(subtask, task, resultContextId, rid)
  const occurrence = resolveResultOccurrence(task.recurrence_ts)
  const storageKey = courseStorageKey(subtask.st_id, resultContextId, occurrence, uid || '')

  const post = useCallback(
    (type: AppMessageType, extra?: { id?: string; replyTo?: string; payload?: unknown }) => {
      const frame = frameRef.current?.contentWindow
      if (!activeRef.current || !frame || !origin) return
      frame.postMessage(appMessage(type, extra), origin)
    },
    [frameRef, origin]
  )

  const close = useCallback(() => {
    if (flushRef.current) window.clearTimeout(flushRef.current.timer)
    flushRef.current = null
    activeRef.current = false
    onCloseRef.current()
  }, [])

  const requestClose = useCallback(() => {
    if (!activeRef.current || flushRef.current) return

    // nothing was started yet: nothing to save
    if (!initSentRef.current) {
      close()
      return
    }
    setClosing(true)
    const id = `flush-${Date.now()}`
    flushRef.current = { id, timer: window.setTimeout(close, COURSE_FLUSH_TIMEOUT_MS) }
    post('flush', { id })
  }, [close, post])

  const handle = async (message: ICourseMessage) => {
    const payload = message.payload || {}

    if (message.type === 'ready') {
      // the player repeats `ready` until it gets an answer
      if (initSentRef.current) return
      initSentRef.current = true
      try {
        const progress = await fetchCourseProgress(request)
        post('init', {
          payload: { progress, learner: { id: uid, firstName, lastName: lastName || '' }, storageKey },
        })
      } catch (error) {
        // the player shows the error and may retry with a new `ready`
        initSentRef.current = false
        post('init-error', { payload: toBridgeError(error) })
      }
      return
    }

    if (message.type === 'load') {
      try {
        const progress = await fetchCourseProgress(request)
        post('load-result', { replyTo: message.id, payload: { ok: true, progress } })
      } catch (error) {
        post('load-result', { replyTo: message.id, payload: { ok: false, ...toBridgeError(error) } })
      }
      return
    }

    if (message.type === 'save') {
      try {
        const answer = await saveCourseProgress(request, payload.data || {})
        post('save-result', { replyTo: message.id, payload: { ok: true, answer } })
      } catch (error) {
        post('save-result', { replyTo: message.id, payload: { ok: false, ...toBridgeError(error) } })
      }
      return
    }

    if (message.type === 'finished') {
      setFinished(true)
      return
    }

    if (message.type === 'flush-result') {
      if (flushRef.current && message.replyTo === flushRef.current.id) close()
      return
    }

    if (message.type === 'close') close()
  }

  useEffect(() => {
    onCloseRef.current = onClose
    handleRef.current = handle
  })

  useEffect(() => {
    activeRef.current = true
    initSentRef.current = false

    const listener = (event: MessageEvent) => {
      const message = parseCourseMessage(event, origin, frameRef.current?.contentWindow)
      if (message) handleRef.current(message)
    }
    window.addEventListener('message', listener)

    return () => {
      window.removeEventListener('message', listener)
      if (flushRef.current) window.clearTimeout(flushRef.current.timer)
      flushRef.current = null
      activeRef.current = false
    }
  }, [frameRef, origin])

  return { requestClose, finished, closing }
}
